import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
    Button, 
    IconButton, 
    Typography, 
    Divider, 
    Tooltip, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions
} from '@material-ui/core';
import {
    FormatBold,
    FormatItalic,
    FormatListBulleted,
    FormatListNumbered,
    Code,
    Link,
    Title,
    Delete,
    Visibility,
    VisibilityOff,
    AttachFile,
    Description
} from '@material-ui/icons';
import AuthenticatedImage from './AuthenticatedImage';
import PdfUploadService from '../services/PdfUploadService';
import './RichTextAnswer.css';

const RichTextAnswer = React.memo(({ 
    question, 
    answer, 
    onAnswerChange, 
    disabled = false,
    examCode,
    userId
}) => {
    const [text, setText] = useState(answer?.text || '');
    const [pdfFile, setPdfFile] = useState(answer?.pdfFile || null); // PDF file object
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showPdfPreviewDialog, setShowPdfPreviewDialog] = useState(false);
    const fileInputRef = useRef(null);
    const editorRef = useRef(null);
    const previewButtonRef = useRef(null);

    // Update parent when text or PDF change - increased debounce for better performance
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const richTextAnswer = {
                text: text.trim(),
                pdfFile: pdfFile ? {
                    fileName: pdfFile.fileName,
                    file: pdfFile.file // Keep the file object for upload
                } : null,
                timestamp: new Date().toISOString()
            };
            onAnswerChange(question.id, richTextAnswer);
        }, 500); // Increased debounce to 500ms for better performance

        return () => clearTimeout(timeoutId);
    }, [text, pdfFile, question.id, onAnswerChange]);

    // Handle PDF file selection
    const handlePdfSelect = useCallback((file) => {
        if (!file) return;
        
        // Validate file type
        if (file.type !== 'application/pdf') {
            alert('Please select a PDF file.');
            return;
        }
        
        // Validate file size (max 1MB) - use service method for consistency
        const validation = PdfUploadService.validateFileSize(file);
        if (!validation.isValid) {
            alert(validation.error);
            return;
        }
        
        const pdfObject = {
            fileName: file.name,
            file: file // Keep file object for upload
        };
        
        setPdfFile(pdfObject);
    }, []);

    // Remove PDF file
    const removePdf = useCallback(() => {
        setPdfFile(null);
    }, []);

    // Format file size
    const formatFileSize = useCallback((bytes) => {
        if (!bytes || bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    // Handle preview modal open/close with proper focus management
    const handlePreviewOpen = useCallback(() => {
        setShowPreviewModal(true);
    }, []);

    const handlePreviewClose = useCallback(() => {
        setShowPreviewModal(false);
        // Return focus to the preview button after modal closes
        setTimeout(() => {
            if (previewButtonRef.current) {
                previewButtonRef.current.focus();
            }
        }, 100);
    }, []);

    // Rich text editor functions - optimized with useCallback
    const applyFormat = useCallback((format) => {
        const editor = editorRef.current;
        if (!editor) return;

        // Focus the editor first
        editor.focus();
        
        // Get current selection
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        const selectedText = range.toString();
        
        if (!selectedText) {
            // If no text is selected, show a message
            alert('Please select some text to format.');
            return;
        }

        // Apply formatting
        switch (format) {
            case 'bold':
                document.execCommand('bold', false, null);
                break;
            case 'italic':
                document.execCommand('italic', false, null);
                break;
            case 'code':
                document.execCommand('formatBlock', false, '<pre>');
                break;
            case 'link':
                const url = prompt('Enter URL:');
                if (url) {
                    document.execCommand('createLink', false, url);
                }
                break;
            case 'heading':
                document.execCommand('formatBlock', false, '<h3>');
                break;
            case 'list':
                document.execCommand('insertUnorderedList', false, null);
                break;
            case 'numbered':
                document.execCommand('insertOrderedList', false, null);
                break;
        }
        
        // Update the text state
        setText(editor.innerHTML);
    }, []);

    // Handle editor content changes
    const handleEditorChange = useCallback(() => {
        const editor = editorRef.current;
        if (editor) {
            setText(editor.innerHTML);
        }
    }, []);

    // Initialize editor content
    useEffect(() => {
        const editor = editorRef.current;
        if (editor && text && !editor.innerHTML) {
            editor.innerHTML = text;
        }
    }, [text]);

    const formatButtons = [
        { icon: <FormatBold />, tooltip: 'Bold (Ctrl+B)', format: 'bold' },
        { icon: <FormatItalic />, tooltip: 'Italic (Ctrl+I)', format: 'italic' },
        { icon: <Code />, tooltip: 'Code Block', format: 'code' },
        { icon: <Link />, tooltip: 'Insert Link', format: 'link' },
        { icon: <Title />, tooltip: 'Heading', format: 'heading' },
        { icon: <FormatListBulleted />, tooltip: 'Bullet List', format: 'list' },
        { icon: <FormatListNumbered />, tooltip: 'Numbered List', format: 'numbered' }
    ];

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey || e.metaKey) {
                switch (e.key.toLowerCase()) {
                    case 'b':
                        e.preventDefault();
                        applyFormat('bold');
                        break;
                    case 'i':
                        e.preventDefault();
                        applyFormat('italic');
                        break;
                }
            }
        };

        const editor = editorRef.current;
        if (editor) {
            editor.addEventListener('keydown', handleKeyDown);
            editor.addEventListener('input', handleEditorChange);
            editor.addEventListener('paste', handleEditorChange);
            return () => {
                editor.removeEventListener('keydown', handleKeyDown);
                editor.removeEventListener('input', handleEditorChange);
                editor.removeEventListener('paste', handleEditorChange);
            };
        }
    }, [applyFormat, handleEditorChange]);


    return (
        <div className="rich-text-answer">
            {/* Question Image Display */}
            {question.imageUrl && (
                <div style={{ 
                    marginBottom: 16, 
                    padding: 24, 
                    background: '#f8f9fa', 
                    border: '1px solid #e9ecef',
                    borderRadius: 8 
                }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: '#1976d2' }}>
                        📷 Question Image
                    </Typography>
                    <AuthenticatedImage 
                        imageUrl={question.imageUrl}
                        questionId={question.id}
                        alt="Question" 
                        style={{ 
                            maxHeight: 650, // 30% larger image for better visibility
                            width: '100%'
                        }} 
                    />
                </div>
            )}
            
            {/* Rich Text Editor with Inline Toolbar */}
            <div className="text-editor-container">
                <div className="inline-toolbar">
                    <div className="toolbar-buttons">
                        {formatButtons.map((btn, index) => (
                            <Tooltip key={index} title={btn.tooltip}>
                                <IconButton
                                    size="small"
                                    onClick={() => applyFormat(btn.format)}
                                    className="format-button"
                                    disabled={disabled}
                                >
                                    {btn.icon}
                                </IconButton>
                            </Tooltip>
                        ))}
                        <Divider orientation="vertical" flexItem style={{ margin: '0 6px' }} />
                        <Tooltip title="Attach PDF">
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setTimeout(() => {
                                        fileInputRef.current?.click();
                                    }, 100);
                                }}
                                disabled={disabled}
                                className="format-button"
                                color="primary"
                            >
                                <AttachFile />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
                
                <div
                    ref={editorRef}
                    className="rich-text-editor"
                    contentEditable={!disabled}
                    onInput={handleEditorChange}
                    onPaste={handleEditorChange}
                    data-placeholder="Enter your answer here... You can use the toolbar above for formatting or attach a PDF using the attachment button."
                    data-gramm="false"
                    data-gramm_editor="false"
                    data-enable-grammarly="false"
                    spellCheck="false"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                />
            </div>

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                        handlePdfSelect(file);
                    }
                    if (e.target) {
                        e.target.value = '';
                    }
                }}
                style={{ display: 'none' }}
            />

            {/* PDF Attachment Display */}
            {pdfFile && (
                <div className="pdf-attachment-display" style={{ 
                    marginTop: 16, 
                    padding: 16, 
                    background: '#f8f9fa', 
                    border: '1px solid #e9ecef',
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Description color="primary" style={{ fontSize: 32 }} />
                        <div>
                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                {pdfFile.fileName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {pdfFile.file ? formatFileSize(pdfFile.file.size) : 'Unknown size'}
                            </Typography>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Tooltip title="Preview PDF">
                            <IconButton
                                size="small"
                                onClick={() => setShowPdfPreviewDialog(true)}
                                disabled={disabled}
                            >
                                <Visibility />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove PDF">
                            <IconButton
                                size="small"
                                onClick={removePdf}
                                disabled={disabled}
                            >
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            )}

            {/* Preview Button */}
            <div className="preview-section">
                <Button
                    ref={previewButtonRef}
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<Visibility />}
                    onClick={handlePreviewOpen}
                    className="preview-button"
                    aria-label="Preview answer"
                    disabled={!text.trim() && !pdfFile}
                >
                    Preview Answer
                </Button>
            </div>

            {/* Preview Modal */}
            <Dialog 
                open={showPreviewModal} 
                onClose={handlePreviewClose}
                maxWidth="md"
                fullWidth
                className="preview-modal"
                disableRestoreFocus
                disableAutoFocus
                disableEnforceFocus
                keepMounted={false}
                aria-labelledby="preview-dialog-title"
                aria-describedby="preview-dialog-description"
            >
                <DialogTitle id="preview-dialog-title">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Answer Preview</Typography>
                        <IconButton 
                            onClick={handlePreviewClose}
                            aria-label="Close preview"
                        >
                            <VisibilityOff />
                        </IconButton>
                    </div>
                </DialogTitle>
                <DialogContent id="preview-dialog-description">
                    <div className="preview-content">
                        {text ? (
                            <div className="html-preview" dangerouslySetInnerHTML={{ __html: text }} />
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No text content to preview
                            </Typography>
                        )}
                        
                        {pdfFile && (
                            <div className="preview-pdf">
                                <Typography variant="subtitle2" className="preview-pdf-title">
                                    PDF Attachment
                                </Typography>
                                <div className="preview-pdf-info">
                                    <Description color="primary" style={{ marginRight: 8 }} />
                                    <div>
                                        <Typography variant="body2" className="preview-pdf-name">
                                            {pdfFile.fileName}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {pdfFile.file?.size ? formatFileSize(pdfFile.file.size) : 'Unknown size'}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePreviewClose} color="primary" aria-label="Close preview dialog">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* PDF Preview Dialog */}
            <Dialog 
                open={showPdfPreviewDialog} 
                onClose={() => setShowPdfPreviewDialog(false)}
                maxWidth="lg"
                fullWidth
                className="pdf-preview-dialog"
            >
                <DialogTitle>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">PDF Preview</Typography>
                        <Typography variant="caption" color="textSecondary">
                            {pdfFile?.fileName}
                        </Typography>
                    </div>
                </DialogTitle>
                <DialogContent>
                    {pdfFile && pdfFile.file && (
                        <div className="pdf-preview-container" style={{ marginTop: 16 }}>
                            <iframe
                                src={URL.createObjectURL(pdfFile.file)}
                                width="100%"
                                height="600px"
                                style={{ border: 'none', borderRadius: '8px' }}
                                title={`Preview of ${pdfFile.fileName}`}
                            />
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setShowPdfPreviewDialog(false)} 
                        color="primary"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
});

export default RichTextAnswer; 